import {
  BusinessProcess,
  PlanningSection,
  WorkflowSchedule,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

/**
 * CellKey = `${businessProcess}|${planningSection ?? ''}|${workflowSchedule ?? ''}|${areaCode}`
 * 入札 2 業務処理は断面・スケジュールを持たないので、section = PlanningSection.UNSPECIFIED,
 * schedule = WorkflowSchedule.UNSPECIFIED とする。proto の cell が section / schedule を
 * 持つ形のまま変えないため、4 要素の形式を保つ。
 */
export type CellKey = `${number}|${number}|${number}|${number}`

export function makeCellKey(
  businessProcess: BusinessProcess,
  section: PlanningSection,
  schedule: WorkflowSchedule,
  areaCode: AreaCode,
): CellKey {
  return `${businessProcess}|${section}|${schedule}|${areaCode}`
}

/**
 * makeCellKey の対の decode。encode/decode 対を同一 module に閉じる seam。
 * 位置 index (businessProcess/section/schedule/area) の意味は本関数内にのみ存在する。
 * round-trip 保証 (parseCellKey(makeCellKey(x)) === x) は processMap.test.ts。
 */
export function parseCellKey(key: CellKey): {
  businessProcess: BusinessProcess
  section: PlanningSection
  schedule: WorkflowSchedule
  areaCode: AreaCode
} {
  const [bp, section, schedule, area] = key.split('|').map(Number)
  return {
    businessProcess: bp as BusinessProcess,
    section: section as PlanningSection,
    schedule: schedule as WorkflowSchedule,
    areaCode: area as AreaCode,
  }
}

/** section=UNSPECIFIED, schedule=UNSPECIFIED の CellKey (入札 2 業務処理のセル) */
export function makeNoSectionCellKey(
  businessProcess: BusinessProcess,
  areaCode: AreaCode,
): CellKey {
  return makeCellKey(
    businessProcess,
    PlanningSection.UNSPECIFIED,
    WorkflowSchedule.UNSPECIFIED,
    areaCode,
  )
}

/** 9 エリア (沖縄なし) = JEPX 市場対応 */
export const AREAS_JEPX: readonly AreaCode[] = [
  AreaCode.HOKKAIDO,
  AreaCode.TOHOKU,
  AreaCode.TOKYO,
  AreaCode.CHUBU,
  AreaCode.HOKURIKU,
  AreaCode.KANSAI,
  AreaCode.CHUGOKU,
  AreaCode.SHIKOKU,
  AreaCode.KYUSHU,
] as const

export const AREA_CODE_LABELS: Readonly<Record<AreaCode, string>> = {
  [AreaCode.UNSPECIFIED]: '',
  [AreaCode.HOKKAIDO]: '北海道',
  [AreaCode.TOHOKU]: '東北',
  [AreaCode.TOKYO]: '東京',
  [AreaCode.CHUBU]: '中部',
  [AreaCode.HOKURIKU]: '北陸',
  [AreaCode.KANSAI]: '関西',
  [AreaCode.CHUGOKU]: '中国',
  [AreaCode.SHIKOKU]: '四国',
  [AreaCode.KYUSHU]: '九州',
  [AreaCode.OKINAWA]: '沖縄',
}

export type ProcessDef = {
  businessProcess: BusinessProcess
  /** i18n key (page:adminApprovalFlow.process.*) */
  i18nKey: string
  areas: readonly AreaCode[]
}

/** 承認フロー画面が扱う入札 2 業務処理 (表示順) */
export const PROCESS_DEFS: readonly ProcessDef[] = [
  {
    businessProcess: BusinessProcess.SPOT_BID,
    i18nKey: 'page:adminApprovalFlow.process.spotBid',
    areas: AREAS_JEPX,
  },
  {
    businessProcess: BusinessProcess.INTRADAY_BID,
    i18nKey: 'page:adminApprovalFlow.process.intradayBid',
    areas: AREAS_JEPX,
  },
] as const
